import _ from 'lodash'
import { encodeWord, decodeWord, removePunctuation } from './misc'
import { NEW_WORD_DEFAULTS } from '../apigateway/vocabulary'
import {
  NEW_SENTENCE_DEFAULTS,
  SENTENCE_INTERVAL_LOWER_BOUND,
} from '../constants'

export const SCORES_STACK_LIMIT = 20
const SCORE_THRESHOLDS = {
  phrase: 0.7,
  word: 0.7,
}
export const WORD_INTERACTIONS = { // enum by hand
  None: 0,
  ThumbDown: 1,
  ThumbUp: 2,
  Translate: 3,
  TranslateSingleOnly: 4, // click-to-translate (CTT)
  TranslateSingleAndAll: 5,
  weakThumbUp: 6,
  weakThumbDown: 7,
  weakTranslate: 8,
  weakTranslateSingleOnly: 9,
  weakTranslateSingleAndAll: 10,
}
const INTERACTION_SCORES = {
  [WORD_INTERACTIONS.ThumbDown]: 0.5,
  [WORD_INTERACTIONS.ThumbUp]: 1,
  [WORD_INTERACTIONS.Translate]: 0.8,
  [WORD_INTERACTIONS.TranslateSingleOnly]: 0,
  [WORD_INTERACTIONS.TranslateSingleAndAll]: 0,
  [WORD_INTERACTIONS.weakThumbUp]: 1,
  [WORD_INTERACTIONS.weakThumbDown]: 0,
  [WORD_INTERACTIONS.weakTranslate]: 0.7,
  [WORD_INTERACTIONS.weakTranslateSingleOnly]: 0,
  [WORD_INTERACTIONS.weakTranslateSingleAndAll]: 0,
}

export function getScoreFromProps(wordProps) {
  if (!wordProps || !wordProps.interactions || !wordProps.interactions.length) return 0
  return _.meanBy(wordProps.interactions, (interaction) => INTERACTION_SCORES[interaction])
}

/**
 * Updates vocabulary and morphs for each word
 */
export function updateVocabulary(vocabMap, morphMap, content, interactions) {
  const wordArr = content.map(({ word }) => word)
  const encodedWordsArr = _.map(_.compact(wordArr), encodeWord)
  const morphArr = content.map(({ morphs }) => morphs)
  // morphArr = [[morph 1,morph 2], [1,2...]...]

  encodedWordsArr.forEach((word, idx) => {
    const currentInteraction = interactions[idx]
    const wordProps = _.defaultsDeep(vocabMap[word], NEW_WORD_DEFAULTS) // new word
    updateForInteraction(wordProps, currentInteraction)
    _.assign(vocabMap, { [word]: wordProps })
  })

  morphArr.forEach((morphs, idx) => {
    const encodedMorphArr = _.map(_.compact(morphs), encodeWord)
    _.map(encodedMorphArr, (encodedMorph) => {
      const currentInteraction = interactions[idx]
      const morphProps = _.defaultsDeep(morphMap[encodedMorph], NEW_WORD_DEFAULTS) // new word
      updateForInteraction(morphProps, currentInteraction)
      _.assign(morphMap, { [encodedMorph]: morphProps })
    })
  })

  return { vocab: vocabMap, morphs: morphMap }
}

function pushLimit(arr, x) {
  return _.slice(_.concat(arr, x), -SCORES_STACK_LIMIT)
}

/**
 * Pushes recent interaction into queue: mutates, no return
 */
export function updateForInteraction(vocab, interaction) {
  if (interaction === WORD_INTERACTIONS.None) return
  _.assign(vocab, {
    interactions: pushLimit(vocab.interactions, interaction),
    interactionCount: (vocab.interactionCount || vocab.interactions.length) + 1,
    interactionDates: pushLimit(vocab.interactionDates, Date.now()),
  })
}

export function isKnownWord(wordProps) {
  return getScoreFromProps(wordProps) >= SCORE_THRESHOLDS.word
}

/**
 * Surface card if any morphs of the main word has a score lower than 0.7
 */
export function shouldCardBeSurfaced(card, morphs) {
  switch (card.type) {
    case 'word': {
      const { word } = card
      const isWeak = word.morphs.some((morph) => getScoreFromProps(morphs[encodeWord(morph)]) < 0.7)
      return isWeak
    }
    default:
      return true
  }
}

export function setSentenceProps(
  sentence, interaction, encodedSentence, moduleLang, vocab, moduleId,
) {
  const sentenceProps = _.defaultsDeep(sentence[encodedSentence], NEW_SENTENCE_DEFAULTS)
  const sentenceArr = JSON.parse(decodeWord(encodedSentence))[moduleLang].split(' ')
  const averageSentenceScore = sentenceArr.reduce((score, word) => score
    + getScoreFromProps(vocab[removePunctuation(word)]), 0) / sentenceArr.length
  let newInterval = sentenceProps.interval
  if (interaction === WORD_INTERACTIONS.ThumbUp) {
    newInterval = _.round(sentenceProps.interval * (averageSentenceScore + 1.5), 1)
  } else if (interaction === WORD_INTERACTIONS.ThumbDown) {
    newInterval = SENTENCE_INTERVAL_LOWER_BOUND // 0.67
  }
  _.assign(sentenceProps, {
    interval: Math.max(newInterval, SENTENCE_INTERVAL_LOWER_BOUND),
    interactions: pushLimit(sentenceProps.interactions, interaction),
    interactionCount: (sentenceProps.interactionCount || sentenceProps.interactions.length) + 1,
    interactionDates: pushLimit(sentenceProps.interactionDates, Date.now()),
  })
  if (moduleId) {
    _.assign(sentenceProps, { source: moduleId })
  }
  return sentenceProps
}
